/**
 * Mobile board chrome: floating glass header pills instead of the desktop
 * HUD layout.
 *
 * Left (most of the width): the focal breadcrumb in a translucent pill,
 * horizontally scrollable when the path is deeper than the screen. Right:
 * circular glass buttons — search (host ⌘F modal) and settings. Settings
 * opens a bottom SHEET (the 2026 phone idiom for transient settings):
 * dimmed tap-to-close scrim, grabber handle, grouped borderless rows,
 * spring-curve entrance. The host supplies the sheet content as a render
 * prop receiving `close`, so picking a setting dismisses it. The board
 * contributes its own smart-view switch row when the operator is
 * available.
 */
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Crosshair, Search, SlidersHorizontal } from "lucide-react";
import type { FocalStep } from "@archlang/scene/board";
import { useTranslation } from "../i18n";
import { Breadcrumb } from "./Breadcrumb";

export interface MobileBoardHeaderProps {
  readonly steps: readonly FocalStep[];
  readonly onNavigate: (index: number) => void;
  /** Host settings content for the sheet. Call `close` after applying a
   *  pick so the sheet gets out of the way. */
  readonly menu?: (close: () => void) => ReactNode;
  /** Smart-view (camera operator) toggle — board-owned; null hides the row. */
  readonly smartView: { readonly on: boolean; readonly onToggle: () => void } | null;
  /** Open the host's search (the ⌘F modal) — phones have no ⌘F. Absent →
   *  no search button. */
  readonly onSearch?: () => void;
}

const GLASS_BUTTON =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 " +
  "bg-slate-900/(--tint-strong) text-slate-300 shadow-lg shadow-black/30 backdrop-blur-xl " +
  "transition-all duration-200 active:scale-95 hover:text-white " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950";

export function MobileBoardHeader({
  steps,
  onNavigate,
  menu,
  smartView,
  onSearch,
}: MobileBoardHeaderProps) {
  const { t } = useTranslation("mobileHeader");
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  // The crumb pill hugs its content and grows with the path; once it hits
  // the available width it scrolls — pinned to the END (deepest space
  // rightmost). Layout effect = re-pinned BEFORE paint on every path
  // change, so a dive never flashes the stale scroll position; the
  // ResizeObserver re-pins on rotation/resize too.
  const crumbRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = crumbRef.current;
    if (!el) return;
    const stick = () => {
      el.scrollLeft = el.scrollWidth;
    };
    stick();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(stick);
    ro.observe(el);
    return () => ro.disconnect();
  }, [steps]);

  return (
    <>
      <div
        data-hud
        data-hud-region="board-header"
        className="absolute inset-x-0 top-0 z-chrome flex items-center gap-2 px-2 pt-2"
      >
        <div
          ref={crumbRef}
          className="mr-auto flex h-10 min-w-0 max-w-full items-center overflow-x-auto rounded-full border border-white/10 bg-slate-900/(--tint-strong) px-2 shadow-lg shadow-black/30 backdrop-blur-xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <Breadcrumb steps={steps} onNavigate={onNavigate} />
        </div>
        {onSearch && (
          <button type="button" aria-label={t("search")} onClick={onSearch} className={GLASS_BUTTON}>
            <Search width={18} height={18} strokeWidth={2} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          aria-label={t("boardSettings")}
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className={GLASS_BUTTON}
        >
          <SlidersHorizontal width={18} height={18} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {open &&
        // Portaled to <body> with `fixed`: the sheet must rise from the
        // REAL viewport bottom (over the tab bar), and the board's
        // ancestors (blur/transform layers) would otherwise re-anchor a
        // fixed/absolute overlay to the board area.
        createPortal(
        <div
          data-hud
          data-hud-region="board-menu"
          role="dialog"
          aria-label={t("boardSettings")}
          className="fixed inset-0 z-overlay"
        >
          {/* Scrim — tap anywhere outside the sheet to dismiss. */}
          <div
            className="arch-scrim-anim absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            onClick={close}
          />
          <div className="arch-sheet-anim absolute inset-x-0 bottom-0 flex max-h-[85%] flex-col rounded-t-3xl border-t border-white/10 bg-slate-900/(--tint-solid) shadow-2xl shadow-black/60 backdrop-blur-2xl">
            {/* Grabber — visual affordance; the scrim is the close target. */}
            <div aria-hidden="true" className="mx-auto mt-2.5 h-1 w-9 shrink-0 rounded-full bg-white/20" />
            <div className="flex shrink-0 items-center justify-between px-5 pb-1 pt-3">
              <span className="text-base font-semibold text-slate-100">{t("boardSettings")}</span>
              <button
                type="button"
                aria-label={t("closeSettings")}
                onClick={close}
                className="rounded-full bg-white/10 px-3.5 py-1.5 text-[13px] font-medium text-slate-300 transition-all duration-200 active:scale-95 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
              >
                {t("done")}
              </button>
            </div>
            <div className="flex-1 space-y-6 overflow-y-auto px-4 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-3">
              {smartView && (
                <MenuSection title={t("camera")}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={smartView.on}
                    onClick={smartView.onToggle}
                    className="flex w-full items-center justify-between rounded-2xl bg-white/[0.06] px-4 py-3 text-[15px] text-slate-200 transition-colors active:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                  >
                    <span className="flex items-center gap-2.5">
                      <Crosshair width={17} height={17} strokeWidth={2} aria-hidden="true" />
                      {t("smartViewFollowEdits")}
                    </span>
                    <SwitchKnob on={smartView.on} />
                  </button>
                </MenuSection>
              )}
              {menu?.(close)}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/** iOS-style switch track + knob — purely visual; the row is the control. */
function SwitchKnob({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={
        "relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors duration-200 " +
        (on ? "bg-arch-500" : "bg-white/15")
      }
    >
      <span
        className={
          "absolute h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 " +
          (on ? "translate-x-[18px]" : "translate-x-0.5")
        }
      />
    </span>
  );
}

/** Shared section chrome for the settings sheet — exported so the host's
 *  menu content matches the board-owned rows. */
export function MenuSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
